import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Car} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

// IRS Section 179 luxury auto limits (2023 approximate)
const IRS_LUXURY_CAPS: Record<number, number> = {
  1: 12200, 2: 19500, 3: 11700, 4: 6960,
}

const VEHICLE_TYPES = [
  { label: "Passenger Car (luxury auto limits apply)", life: 5, luxuryCap: true },
  { label: "Light Truck / SUV > 6,000 lbs GVW (no luxury cap)", life: 5, luxuryCap: false },
  { label: "Heavy SUV > 6,000 lbs (Section 179 capped at $28,900)", life: 5, luxuryCap: false },
  { label: "Cargo Van / Work Van (no luxury cap)", life: 5, luxuryCap: false },
  { label: "Motorcycle (business use)", life: 5, luxuryCap: false },
]

export default function BusinessVehiclesCalculator() {
  const [cost, setCost] = useState("")
  const [businessUse, setBusinessUse] = useState("100")
  const [vType, setVType] = useState(0)
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{
    adjCost: number; yr1: number; totalDepr: number; applyLuxuryCap: boolean
  } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const buPct = parseFloat(businessUse) / 100
    const { life, luxuryCap } = VEHICLE_TYPES[vType]
    if (isNaN(c) || c <= 0) { setError("Please enter a valid vehicle cost."); return }
    if (isNaN(buPct) || buPct <= 0 || buPct > 1) { setError("Business use % must be between 1 and 100."); return }

    const adjCost = c * buPct
    const ddbRate = 2 / life
    const rows: ScheduleRow[] = []
    let bv = adjCost
    let accumulated = 0

    for (let i = 1; i <= life; i++) {
      const begin = bv
      let depr = bv * ddbRate
      const slD = bv / (life - i + 1)
      if (slD > depr) depr = slD
      if (depr < 0) depr = 0

      if (luxuryCap && IRS_LUXURY_CAPS[i]) {
        const cap = IRS_LUXURY_CAPS[i] * buPct
        if (depr > cap) depr = cap
      }

      accumulated += depr
      bv -= depr
      rows.push({ year: i, beginningValue: begin, depreciation: depr, accumulatedDepreciation: accumulated, endingBookValue: Math.max(0, bv) })
    }

    setSchedule(rows)
    setResults({ adjCost, yr1: rows[0]?.depreciation ?? 0, totalDepr: accumulated, applyLuxuryCap: luxuryCap })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-teal-600 rounded-lg p-2">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-teal-600 uppercase tracking-wide">Asset Specific</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Business Vehicle & IRS Auto Depreciation Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate IRS-compliant depreciation for business vehicles including cars, trucks,
            SUVs, and vans. Applies luxury auto limitations where required.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter Vehicle Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
              <select value={vType} onChange={(e) => setVType(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900">
                {VEHICLE_TYPES.map((v, i) => <option key={v.label} value={i}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Use %</label>
              <input type="number" value={businessUse} onChange={(e) => setBusinessUse(e.target.value)} placeholder="e.g. 80"
                min="1" max="100"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Purchase Price ($)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 45000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button onClick={calculate}
            className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Calculate Vehicle Depreciation
          </button>

          {results !== null ? (
            <div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-teal-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-teal-700">{fmt(results.adjCost)}</div>
                  <div className="text-sm text-gray-600 mt-1">Business-Use Basis</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{fmt(results.yr1)}</div>
                  <div className="text-sm text-gray-600 mt-1">Year 1 Deduction</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-gray-700">{fmt(results.totalDepr)}</div>
                  <div className="text-sm text-gray-600 mt-1">Total Depreciation</div>
                </div>
              </div>
              {results.applyLuxuryCap ? (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  ⚠️ <strong>Luxury Auto Limits Applied:</strong> IRS Section 280F limits apply to passenger cars.
                  Year 1 cap: ~$12,200, Year 2: ~$19,500, Year 3+: ~$11,700/$6,960. After the 5-year recovery period,
                  you may continue deducting $6,960/year until fully recovered.
                </div>
              ) : null}
            </div>
          ) : null}
          {schedule.length > 0 ? <DepreciationScheduleTable rows={schedule} onExport={() => exportToCSV(schedule, "vehicle-depreciation-schedule.csv")} /> : null}
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">IRS Auto Depreciation Rules — Section 280F</h2>
          <p className="text-gray-700 mb-4">
            Business vehicles are generally 5-year MACRS property. However, <strong>passenger cars</strong>
            are subject to <strong>Section 280F "luxury auto" limitations</strong> that cap annual
            depreciation regardless of the vehicle's cost. Vehicles over 6,000 lbs GVW (SUVs,
            trucks, vans) are generally exempt from these caps.
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Year</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Luxury Auto Cap (100% business)</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Year 1", "$12,200", "Without bonus depreciation"],
                  ["Year 2", "$19,500", "DDB switching to SL"],
                  ["Year 3", "$11,700", "Continued straight line"],
                  ["Year 4+", "$6,960/year", "Until fully recovered"],
                ].map(([yr, cap, notes], i) => (
                  <tr key={yr} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-3 font-medium text-teal-700">{yr}</td>
                    <td className="px-4 py-3 text-gray-700">{cap}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
            <h3 className="font-semibold text-teal-900 mb-2">Mixed Personal & Business Use</h3>
            <p className="text-teal-800 text-sm">
              Only the <strong>business-use percentage</strong> of a vehicle's cost is
              depreciable. Keep a mileage log to document business use. If business use drops
              below 50%, you must switch to the straight-line ADS method. Consider the
              <strong> standard mileage rate</strong> as an alternative to actual expense
              depreciation (57.5¢/mile in 2023).
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
